import React, { useEffect, useState } from "react";
import { Row, Col, Button, Alert } from "antd";
import { axiosInstant, endpoints } from "../../../api";
import { showError } from "../../../Notifications";
import JobApplicationsTable from "./JobApplicationsTable";
import JobSearchToggle from "./JobSearchToggle";
import { Application } from '../helper/interfaces';
import { StatusType } from "../../common";
import ProfileSetup from '../Profile/ProfileSetup';
import { useUser } from '../../../context';
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMyJobApplications } from "../../../api/api";
import { ROUTES } from "../../common";
import Calendar from "../../Calendar";
import ConfettiCelebration from "../../common/ConfettiCelebration";

const CandidateDashboard: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCalendar, setShowCalendar] = useState(false);
  console.log("Current user:", user);

  const refreshApplications = (checkForHired = false) => {
    console.log("Refreshing job applications...");
    setLoading(true);
    fetchMyJobApplications()
      .then(res => {
        const newApplications = res.data;
        
        // Check if any application status changed to 'hired'
        if (checkForHired && applications.length > 0) {
          const newHiredApps = newApplications.filter((newApp: any) => 
            newApp.status === 'hired' && 
            !applications.find((oldApp: any) => oldApp._id === newApp._id && oldApp.status === 'hired')
          );
          
          if (newHiredApps.length > 0) {
            setShowConfetti(true);
            // You could also show a message here
            console.log("🎉 Congratulations! You've been hired!");
          }
        }
        
        setApplications(newApplications);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    console.log("Fetching job applications...");
    refreshApplications();
  }, []);

  useEffect(() => {
    // Listen for refresh events from notifications
    const handleRefresh = () => {
      console.log("Received refresh event from notification");
      refreshApplications(true); // Check for hired status when refreshing from notification
    };

    // Listen for confetti events from notifications
    const handleConfetti = () => {
      console.log("Received confetti event from notification");
      setShowConfetti(true);
    };

    window.addEventListener('refreshApplications', handleRefresh);
    window.addEventListener('showConfetti', handleConfetti);
    
    return () => {
      window.removeEventListener('refreshApplications', handleRefresh);
      window.removeEventListener('showConfetti', handleConfetti);
    };
  }, []);

  
  const handleSendApplication = (applicationId: string, answers?: any) => {
    setLoading(true);
    axiosInstant.post(`/candidates/applications/${applicationId}/submit`, {
      answers,
      // Optionally, backend can fetch resume from profile by userId
    }).then(() => {
      refreshApplications();
    });
  };

  const handleWithdraw = (applicationId: string) => {
    // Call backend to withdraw application, then refresh
  };

  const mappedApplications = (Array.isArray(applications) ? applications : []).map(app => ({
    ...app,
    _id: app._id || app.id || `noid-${Math.random()}`,
    companyName: app.job?.companyName || (app as any).companyName || '',
    position: app.job?.positionTitle || (app as any).positionTitle || '',
    job: {
      ...(app.job || {}),
      predefinedQuestions: (app.job as any)?.predefinedQuestions || [],
      customQuestions: (app.job as any)?.customQuestions || [],
    },
    dateApplied: app.dateApplied || '',
    interviewDate: app.interviewDate || '',
    secondInterviewDate: app.secondInterviewDate || '',
    offerLetter: app.offerLetter || '',
    offerAccepted: app.offerAccepted || false,
    offerRejected: app.offerRejected || false,
    offerAcceptedDate: app.offerAcceptedDate || '',
    offerRejectedDate: app.offerRejectedDate || '',
    offerAcceptedBy: app.offerAcceptedBy || '',
    offerRejectedBy: app.offerRejectedBy || '',
  }));

  const hasPendingApplications = mappedApplications.some(app => app.status === 'pending');

  return (
    <div>
      {hasPendingApplications && (
        <Alert
          type="info"
          showIcon
          style={{ margin: '20px 24px 0', fontSize: '15px' }}
          message={
            <span>
              Action required — you have a pending application.{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, fontWeight: 600, fontSize: '15px' }}
                onClick={() => document.getElementById('applications-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                Go to Application →
              </Button>
            </span>
          }
          description={
            <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Submit your application</strong> — click <strong>Submit Application</strong> on the pending row to answer any pre-screening questions.
              </li>
              <li>
                <strong>Set your availability</strong> — let the recruiter know when you're free for an interview.{' '}
                <a href="/calendar" style={{ fontWeight: 600 }}>Open Calendar →</a>
              </li>
            </ol>
          }
        />
      )}
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          {loading ? (
            <div style={{ textAlign: 'center', marginTop: 40 }}>Loading...</div>
          ) : (
            <>
              <div id="applications-table" />
              <JobApplicationsTable
                applications={mappedApplications}
                onSendApplication={handleSendApplication}
                refetchApplications={refreshApplications}
              />
            </>
          )}
        </Col>
      </Row>
      
      {/* Confetti Celebration */}
      <ConfettiCelebration 
        show={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
        duration={5000}
      />
    </div>
  );
};

export default CandidateDashboard;
